import { UploadOutlined } from "@ant-design/icons";
import {
	Alert,
	Button,
	Form,
	Input,
	Modal,
	Segmented,
	Select,
	Space,
	Typography,
	Upload,
} from "antd";
import type { UploadFile } from "antd/es/upload";
import { invalidateMyTickets } from "hooks/useSupportTickets";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supportTicketApi } from "services/supportTicketApi";
import { posthog } from "utils/posthog";
import { captureRoute, collectDiagnostics } from "./collectDiagnostics";

const { Text, Paragraph } = Typography;

export type ReportKind = "bug" | "feature";

interface Props {
	open: boolean;
	/** Which tab to land on. The Help menu preselects it, so a user who already
	 * knows what they want never pays for the choice. */
	initialKind?: ReportKind;
	onClose: () => void;
}

/**
 * Impact options, written for an accounting product: the worst outcome here is
 * a silently wrong number, not a crash. Deliberately NOT a Priority dropdown —
 * customers cannot calibrate operational urgency, and a client-settable
 * priority is a client-settable SLA target. The server maps these to a
 * priority (SupportTicketsService.priorityForImpact).
 */
const IMPACT_OPTIONS = [
	{
		value: "wrong_numbers",
		label: "A number looks wrong or a reconciliation is incorrect",
	},
	{ value: "blocked", label: "I'm blocked and can't finish my work" },
	{
		value: "workaround",
		label: "It works, but it's slow, confusing, or I need a workaround",
	},
	{ value: "minor", label: "Something looks off but it isn't holding me up" },
];

interface Draft {
	subject?: string;
	body?: string;
	expected?: string;
	impact?: string;
}

export function ReportIssueModal({
	open,
	initialKind = "bug",
	onClose,
}: Props) {
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const [kind, setKind] = useState<ReportKind>(initialKind);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [done, setDone] = useState<{ ticketNumber: string; id: number } | null>(
		null,
	);
	const [partial, setPartial] = useState(false);

	// One draft per kind. Toggling Bug <-> Idea must not destroy what the user
	// already typed — the two forms ask different questions, so a single shared
	// draft would silently drop fields on every switch.
	const drafts = useRef<Record<ReportKind, Draft>>({ bug: {}, feature: {} });

	// The route the user is reporting ABOUT, frozen when the modal opens. The
	// modal is mounted once and survives navigation, so reading the route at
	// submit would attribute the report to wherever they drifted to.
	const routeRef = useRef<string | undefined>(undefined);

	useEffect(() => {
		if (!open) return;
		routeRef.current = captureRoute();
		setKind(initialKind);
		setError(null);
		setDone(null);
		setPartial(false);
		form.setFieldsValue(drafts.current[initialKind]);
		try {
			posthog.capture("report_issue_opened", {
				kind: initialKind,
				route: routeRef.current,
			});
		} catch {
			// analytics must never block the modal
		}
	}, [open, initialKind, form]);

	const isBug = kind === "bug";

	const switchKind = (next: ReportKind) => {
		drafts.current[kind] = form.getFieldsValue();
		setKind(next);
		form.resetFields();
		form.setFieldsValue(drafts.current[next]);
	};

	const reset = () => {
		drafts.current = { bug: {}, feature: {} };
		form.resetFields();
		setFileList([]);
		setError(null);
		setDone(null);
		setPartial(false);
	};

	const handleClose = () => {
		if (submitting) return; // Escape and backdrop are inert mid-submit
		reset();
		onClose();
	};

	const handleSubmit = async () => {
		let values: Record<string, string>;
		try {
			values = await form.validateFields();
		} catch {
			return; // antd renders field errors itself
		}

		setSubmitting(true);
		setError(null);

		// Upload first and treat a failure as PARTIAL rather than fatal: the
		// user's words are the valuable part, and losing a typed report because
		// an attachment failed is the worse outcome.
		let fileRefs: string[] = [];
		let uploadFailed = false;
		if (fileList.length > 0) {
			const files = fileList
				.map((f) => f.originFileObj)
				.filter(Boolean) as File[];
			if (files.length > 0) {
				try {
					const result = await supportTicketApi.uploadTicketFiles(files);
					// storageKeys, NOT urls. `urls` are signed with a ~15 min TTL, so
					// persisting one stores a link that is permanently dead shortly
					// after the ticket is filed. The API stores keys and re-signs them
					// on every read. (`pages/support/index.tsx` still posts `urls` —
					// same latent bug, its own fix.)
					fileRefs = result.storageKeys ?? result.urls;
				} catch {
					uploadFailed = true;
				}
			}
		}

		// Assemble the prose here so the ticket body reads as a report rather
		// than as form fields. The API only ever sees subject + description.
		const description = isBug
			? [
					values.body,
					values.expected ? `\n\nWhat I expected:\n${values.expected}` : "",
				].join("")
			: [
					values.body,
					values.expected
						? `\n\nHow I handle this today:\n${values.expected}`
						: "",
				].join("");

		try {
			const ticket = await supportTicketApi.createIssueReport({
				kind,
				subject: values.subject,
				description,
				...(isBug && values.impact ? { impact: values.impact } : {}),
				...(fileRefs.length > 0 ? { files: fileRefs } : {}),
				...collectDiagnostics(routeRef.current),
			});
			try {
				posthog.capture("report_issue_submitted", {
					kind,
					route: routeRef.current,
					hadAttachment: fileRefs.length > 0,
				});
			} catch {
				// analytics must never break a successful submit
			}
			setPartial(uploadFailed);
			setDone({ ticketNumber: ticket.ticketNumber, id: ticket.id });
			invalidateMyTickets();
		} catch (err: unknown) {
			// Draft is deliberately preserved — retyping a bug report because the
			// network blipped is how people stop reporting bugs.
			const status = (err as { response?: { status?: number } })?.response
				?.status;
			setError(
				status === 429
					? "You've sent a few reports in quick succession. Give it a couple of minutes and try again — your text is still here."
					: "We couldn't send that just now. Your text is still here — try again in a moment.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	// ── Confirmation. Bug and idea say deliberately different things: a ticket
	// number on an idea reads as a commitment to build it, which it is not. ──
	if (done) {
		return (
			<Modal
				open={open}
				onCancel={handleClose}
				title={isBug ? "Thanks — we've got it" : "Thanks for sharing your idea"}
				footer={
					isBug
						? [
								<Button key="done" onClick={handleClose}>
									Done
								</Button>,
								<Button
									key="view"
									type="primary"
									onClick={() => {
										handleClose();
										navigate(`/support/${done.id}`);
									}}
								>
									View report
								</Button>,
							]
						: [
								<Button key="done" type="primary" onClick={handleClose}>
									Done
								</Button>,
							]
				}
			>
				{partial && (
					<Alert
						type="warning"
						showIcon
						style={{ marginBottom: 12 }}
						message="Your report was sent, but the attachment didn't upload"
						description="Open the report and attach the file again if it matters."
					/>
				)}
				{isBug ? (
					<Paragraph>
						We've logged it as <Text strong>{done.ticketNumber}</Text> and it's
						with the team. You'll see any reply on the Support page.
					</Paragraph>
				) : (
					<Paragraph>
						We've passed it to the product team. We weigh ideas against what
						customers need and where the product is going, so this isn't a
						commitment to build it or a timeline — but it does get read.
					</Paragraph>
				)}
			</Modal>
		);
	}

	return (
		<Modal
			open={open}
			onCancel={handleClose}
			// Only dismiss on backdrop click while the form is untouched. Losing a
			// half-written bug report to a stray click is the exact frustration
			// this feature exists to reduce.
			maskClosable={!form.isFieldsTouched()}
			keyboard={!submitting}
			title="Help us improve Equility"
			okText={isBug ? "Send report" : "Share idea"}
			onOk={handleSubmit}
			confirmLoading={submitting}
			destroyOnClose={false}
			// Render the body even while closed. The modal is mounted for the whole
			// session (so drafts survive navigation), and without this the inner
			// <Form> never mounts, leaving the useForm instance unconnected — antd
			// warns about exactly that on every app load.
			forceRender
		>
			<Space direction="vertical" size="middle" style={{ width: "100%" }}>
				<Segmented
					block
					value={kind}
					onChange={(v) => switchKind(v as ReportKind)}
					aria-label="What would you like to share?"
					options={[
						{ label: "Something's broken", value: "bug" },
						{ label: "I have an idea", value: "feature" },
					]}
				/>

				{error && <Alert type="error" showIcon message={error} />}

				<Form form={form} layout="vertical" requiredMark={false}>
					<Form.Item
						name="subject"
						label={isBug ? "What went wrong?" : "What's your idea?"}
						rules={[
							{
								required: true,
								message: isBug
									? "A one-line summary helps us find it faster"
									: "Give your idea a one-line summary",
							},
						]}
					>
						<Input
							maxLength={200}
							placeholder={
								isBug
									? "e.g. Reconciliation total doesn't match the bank"
									: "e.g. Let me bulk-approve matched transactions"
							}
						/>
					</Form.Item>

					<Form.Item
						name="body"
						label={
							isBug ? "What were you doing?" : "What problem would it solve?"
						}
						rules={[
							{
								required: true,
								message: isBug
									? "Tell us the steps you took, so we can reproduce it"
									: "Tell us the problem behind the idea",
							},
						]}
					>
						<Input.TextArea
							autoSize={{ minRows: 3, maxRows: 10 }}
							placeholder={
								isBug
									? "The steps you took, and what happened instead"
									: "What you're trying to get done"
							}
						/>
					</Form.Item>

					<Form.Item
						name="expected"
						label={
							isBug
								? "What did you expect to happen?"
								: "How do you handle this today?"
						}
					>
						<Input.TextArea
							autoSize={{ minRows: 2, maxRows: 6 }}
							placeholder="Optional, but it helps"
						/>
					</Form.Item>

					{isBug && (
						<Form.Item
							name="impact"
							label="How is this affecting you?"
							initialValue="workaround"
						>
							<Select options={IMPACT_OPTIONS} />
						</Form.Item>
					)}

					<Form.Item
						label="Attachments"
						extra="Only add a screenshot if you're happy for us to see what's on it."
					>
						<Upload
							fileList={fileList}
							onChange={({ fileList: next }) => setFileList(next)}
							beforeUpload={() => false}
							multiple
							maxCount={5}
						>
							<Button icon={<UploadOutlined />}>Select files</Button>
						</Upload>
					</Form.Item>
				</Form>
			</Space>
		</Modal>
	);
}
